import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown, Check, ArrowLeft } from "lucide-react";
import { Reveal, MaskedLines, SectionLabel } from "@/components/Reveal";
import { MS_SERVICES } from "@/data/managedServices";

const Flow = ({ steps, testId }) => (
  <div data-testid={testId} className="flex flex-col items-center gap-1">
    {steps.map((s, i) => (
      <div key={s} className="flex w-full max-w-md flex-col items-center">
        <div className="w-full border border-zinc-900/15 bg-white px-5 py-3 text-center font-mono text-xs uppercase tracking-[0.15em] text-zinc-700">{s}</div>
        {i < steps.length - 1 && <ArrowDown size={14} className="my-1 text-cyan-600" />}
      </div>
    ))}
  </div>
);

export default function ManagedServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const svc = MS_SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    if (svc) document.title = `${svc.nav} — Managed AI Services | Augmented Intelligence Systems`;
  }, [svc]);

  if (!svc) {
    return <main className="flex min-h-screen items-center justify-center pt-24"><p className="font-mono text-sm text-zinc-500">Service not found. <Link to="/managed-services" className="text-cyan-600">View all managed services →</Link></p></main>;
  }

  const related = MS_SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <main data-testid={`ms-detail-${svc.slug}`} className="pt-32">
      <section className="grid-bg border-b border-zinc-900/10 pb-16">
        <div className="mx-auto max-w-7xl px-6">
          <Link to="/managed-services" data-testid="ms-back-link" className="flex w-fit items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 transition-colors hover:text-cyan-600"><ArrowLeft size={14} /> All Managed Services</Link>
          <MaskedLines className="mt-8" lines={[svc.headline]} lineClassName="font-heading text-3xl tracking-tighter leading-[1.1] text-zinc-900 sm:text-5xl" />
          <p className="mt-4 font-mono text-sm uppercase tracking-[0.15em] text-cyan-600">{svc.position}</p>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-zinc-600">{svc.description}</p>
          <button data-testid="ms-detail-cta" onClick={() => navigate("/assessment")} className="btn-primary mt-10">{svc.cta} <ArrowUpRight size={16} /></button>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 py-24 lg:grid-cols-2">
        <Reveal>
          <SectionLabel>What's Included</SectionLabel>
          <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {svc.services.map((s) => (
              <li key={s} className="flex items-start gap-3 border border-zinc-900/10 bg-white px-4 py-3 text-sm text-zinc-700">
                <Check size={15} className="mt-0.5 shrink-0 text-cyan-600" />{s}
              </li>
            ))}
          </ul>
          {svc.deliverables && (
            <>
              <p className="label-mono mt-10">You receive</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {svc.deliverables.map((d) => <span key={d} className="border border-cyan-500/40 bg-cyan-500/5 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-cyan-700">{d}</span>)}
              </div>
            </>
          )}
        </Reveal>
        <Reveal delay={0.1}>
          {svc.workflow ? (
            <>
              <SectionLabel>How It Flows</SectionLabel>
              <div className="mt-8"><Flow steps={svc.workflow} testId="ms-detail-flow" /></div>
              {svc.workflow2 && <div className="mt-8"><Flow steps={svc.workflow2} testId="ms-detail-flow2" /></div>}
            </>
          ) : (
            <div className="border border-zinc-900/10 bg-[#F0F2F5] p-10">
              <SectionLabel>Human + AI, Working Together</SectionLabel>
              <p className="mt-6 text-base leading-relaxed text-zinc-600">AI handles the repetitive volume. Trained human specialists handle judgment, exceptions, and relationships. Automation keeps your CRM, email, calendar, and business systems in sync — so nothing falls through the cracks.</p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-500">You're purchasing a managed outcome — we design, operate, monitor, and continuously optimize the process.</p>
            </div>
          )}
        </Reveal>
      </section>

      <section className="border-t border-zinc-900/10 bg-[#F0F2F5] py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <h2 className="font-heading text-2xl text-zinc-900 sm:text-3xl">Ready to see it on your operation?</h2>
            <div className="flex flex-wrap gap-4">
              <button data-testid="ms-detail-bottom-cta" onClick={() => navigate("/assessment")} className="btn-primary">Schedule a Free AI Operations Assessment <ArrowUpRight size={16} /></button>
              <button onClick={() => navigate("/contact")} className="btn-secondary">Get a Custom Proposal</button>
            </div>
          </div>
          <p className="label-mono mt-14">Related services</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/managed-services/${r.slug}`} className="border border-zinc-900/15 bg-white px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-700 transition-colors hover:border-cyan-500 hover:text-cyan-600">{r.nav}</Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
